import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, ArrowUpRight, ArrowDownRight, ChevronLeft, FileDown } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, StatusBadge, RiskBadge, Empty } from "@/components/app/ui";
import { api, fmtDate, errMsg, can, downloadFile } from "@/lib/api";

const TILES = [["compared", "Controls compared", "text-slate-900"], ["changed", "Changed", "text-blue-700"], ["improved", "Improved", "text-emerald-700"],
  ["regressed", "Regressed", "text-red-700"], ["newly_applicable", "Newly applicable", "text-indigo-700"], ["no_longer_applicable", "No longer applicable", "text-slate-600"]];

const Side = ({ s }) => (!s || !s.applicable ? <span className="text-xs text-slate-400">Not applicable</span> : (
  <div className="space-y-1">
    <div className="flex flex-wrap items-center gap-1.5"><StatusBadge status={s.status} /><RiskBadge risk={s.risk} /></div>
    <div className="text-xs text-slate-600">Answer: <b>{s.answer || "—"}</b> · Evidence: {s.evidence_count}{s.owner ? ` · Owner: ${s.owner}` : ""}</div>
    {s.notes && <div className="line-clamp-2 text-xs text-slate-500">{s.notes}</div>}
  </div>
));

const Direction = ({ d }) => d === "improved" ? <ArrowUpRight className="h-4 w-4 text-emerald-600" />
  : d === "regressed" ? <ArrowDownRight className="h-4 w-4 text-red-600" /> : <ArrowRight className="h-4 w-4 text-slate-300" />;

function CyclePicker({ label, value, onChange, rows, testId }) {
  return (
    <div className="min-w-[260px] flex-1"><div className="label-caps mb-1">{label}</div>
      <Select value={value || undefined} onValueChange={onChange}>
        <SelectTrigger data-testid={testId}><SelectValue placeholder="Select cycle…" /></SelectTrigger>
        <SelectContent>{rows.map((r) => <SelectItem key={r.id} value={r.id}>{r.name} · {fmtDate(r.created_at)}{r.is_current ? " (current)" : ""}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

export default function CompareCycles() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [cycles, setCycles] = useState([]);
  const [data, setData] = useState(null);
  const [changedOnly, setChangedOnly] = useState(true);
  const base = params.get("base"), target = params.get("target");
  useEffect(() => {
    api.get("/assessments").then(({ data: rows }) => {
      setCycles(rows);
      if (!base && !target && rows.length > 1) {
        const t = rows.find((r) => r.is_current) || rows[0];
        const b = rows.find((r) => r.id === t.carried_from) || rows.find((r) => r.id !== t.id);
        setParams({ base: b.id, target: t.id }, { replace: true });
      }
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (base && target) api.get("/assessment-compare", { params: { base, target } }).then((r) => setData(r.data)); }, [base, target]);
  const rows = useMemo(() => (data?.rows || []).filter((r) => !changedOnly || r.changes.length), [data, changedOnly]);
  const set = (k) => (v) => setParams({ base, target, [k]: v });
  const [busy, setBusy] = useState(false);
  const boardPdf = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/reports/board", null, { params: { base, target } });
      await downloadFile(`/reports/${r.id}/download`, "board-change-summary.pdf");
      toast.success("Board change summary generated");
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  return (
    <div>
      <Link to="/assessments" className="mb-2 inline-flex items-center text-xs text-slate-500 hover:text-blue-600"><ChevronLeft className="h-3 w-3" />Assessments</Link>
      <PageHeader title="Compare assessment cycles" subtitle="Control-by-control view of what changed between two cycles — answers, status, risk, evidence and applicability." />
      {cycles.length < 2 ? <Empty text="You need at least two assessment cycles to compare. Start a new cycle from the Assessments page." /> : (<>
        <div className="mb-5 flex flex-wrap items-end gap-3 rounded-lg border border-slate-200 bg-white p-4">
          <CyclePicker label="Baseline (earlier)" value={base} onChange={set("base")} rows={cycles} testId="compare-base-select" />
          <ArrowRight className="mb-2.5 h-4 w-4 text-slate-400" />
          <CyclePicker label="Compared with (later)" value={target} onChange={set("target")} rows={cycles} testId="compare-target-select" />
          <label className="mb-2 flex items-center gap-2 text-sm text-slate-700"><Switch checked={changedOnly} onCheckedChange={setChangedOnly} data-testid="compare-changed-only-switch" />Changed only</label>
          {can(user, "report") && base && target && base !== target && <Button variant="outline" onClick={boardPdf} disabled={busy} data-testid="compare-board-pdf-button"><FileDown className="mr-1.5 h-4 w-4" />{busy ? "Generating…" : "Board PDF"}</Button>}
        </div>
        {data && (<>
          <div className="mb-5 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
            {TILES.map(([k, l, c]) => <div key={k} className="rounded-lg border border-slate-200 bg-white p-4" data-testid={`compare-summary-${k}`}><div className="label-caps">{l}</div><div className={`mt-1 font-display text-2xl font-bold ${c}`}>{data.summary[k]}</div></div>)}
          </div>
          <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white" data-testid="compare-table">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left"><tr>
                <th className="label-caps px-4 py-2.5">Control</th><th className="label-caps px-4 py-2.5">{data.base.name}</th><th className="w-8" />
                <th className="label-caps px-4 py-2.5">{data.target.name}</th><th className="label-caps px-4 py-2.5">What changed</th></tr></thead>
              <tbody>{rows.map((r) => (
                <tr key={r.control_id} className={`border-t align-top ${r.direction === "regressed" ? "bg-red-50/40" : r.direction === "improved" ? "bg-emerald-50/40" : ""}`} data-testid={`compare-row-${r.control_id}`}>
                  <td className="px-4 py-3"><div className="font-mono text-[11px] text-slate-400">{r.control_id} · {r.section_or_rule}</div><div className="font-medium text-slate-800">{r.control_name}</div><div className="text-xs text-slate-500">{r.domain}</div></td>
                  <td className="px-4 py-3"><Side s={r.base} /></td>
                  <td className="py-3"><Direction d={r.direction} /></td>
                  <td className="px-4 py-3"><Side s={r.target} /></td>
                  <td className="px-4 py-3">{r.changes.length ? <div className="flex flex-wrap gap-1">{r.changes.map((c) => <span key={c} className="rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[11px] text-slate-700">{c}</span>)}</div> : <span className="text-xs text-slate-400">Unchanged</span>}</td>
                </tr>
              ))}</tbody>
            </table>
            {!rows.length && <Empty text="No differences between these cycles." />}
          </div>
        </>)}
      </>)}
    </div>
  );
}
